import { useState, useEffect } from 'react';
import apiClient from '../../lib/api';
import { resolveImageUrl } from '../../lib/utils';
import contactHeroImage from '../../assets/images/contact_hero.jpg';

interface ContactInfo {
  phone: string;
  email: string;
  whatsapp: string;
  address: string;
  weekdays_hours: string;
  saturday_hours: string;
  sunday_hours: string;
  facebook_url: string;
  instagram_url: string;
  twitter_url: string;
  youtube_url: string;
  linkedin_url: string;
}

interface Location {
  id: string;
  name: string;
  address: string;
  phone: string;
  email?: string;
  hours?: string;
  map_url: string;
  is_active: number;
}

interface ContactPageSettings {
  hero_title: string;
  hero_subtitle: string;
  hero_image_url: string;
}

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');
  const [contactInfo, setContactInfo] = useState<ContactInfo>({
    phone: '',
    email: '',
    whatsapp: '',
    address: '',
    weekdays_hours: '',
    saturday_hours: '',
    sunday_hours: '',
    facebook_url: '',
    instagram_url: '',
    twitter_url: '',
    youtube_url: '',
    linkedin_url: ''
  });
  const [pageSettings, setPageSettings] = useState<ContactPageSettings>({
    hero_title: 'Get in Touch',
    hero_subtitle: 'Ready to experience premium grooming? Contact us today!',
    hero_image_url: ''
  });
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadContactData();
  }, []);

  const loadContactData = async () => {
    try {
      // 1. Fetch Page Settings (Critical for Hero Title/Subtitle)
      try {
        const pageSettingsData = await apiClient.getContactPageSettings();
        if (pageSettingsData) {
          setPageSettings(pageSettingsData);
        }
      } catch (err) {
        console.error('Failed to load contact page settings:', err);
      }

      // 2. Fetch Contact Info (Social Media, Phone, Email, etc.)
      try {
        const contactData = await apiClient.getContactInfo();
        if (contactData) setContactInfo(contactData);
      } catch (err) {
        console.error('Failed to load contact info:', err);
      }

      // 3. Fetch Locations
      try {
        const locationsData = await apiClient.getActiveContactLocations();
        if (locationsData) setLocations(locationsData);
      } catch (err) {
        console.error('Failed to load locations:', err);
      }

      // 4. Fetch Services (for Dropdown)
      try {
        const servicesData = await apiClient.getServices();
        if (servicesData && Array.isArray(servicesData)) {
          const serviceNames = [...new Set(servicesData.map((service: any) => service.name).filter((name: any) => typeof name === 'string'))] as string[];
          setServices([...serviceNames, 'Other']);
        }
      } catch (err) {
        console.error('Failed to load services:', err);
        setServices(['Hair Cutting', 'Hair Coloring', 'Facial Treatment', 'Beard Styling', 'Complete Makeover', 'Other']);
      }
    } catch (error) {
      console.error('Unexpected error in loadContactData:', error);
    } finally {
      setLoading(false);
    }
  };

  const [services, setServices] = useState<string[]>(['Other']);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Submit form to backend
      await apiClient.submitContactForm(formData);

      setSubmitStatus('success');
      setFormData({ name: '', email: '', phone: '', service: '', message: '' });

      // Send WhatsApp message
      const message = `*New Contact Form Submission*\n\nName: ${formData.name}\nEmail: ${formData.email}\nPhone: ${formData.phone}\nService Interest: ${formData.service}\nMessage: ${formData.message}`;
      const whatsappNumber = contactInfo.whatsapp.replace(/[^\d]/g, '');
      window.open(`https://wa.me/${whatsappNumber}?text=${encodeURIComponent(message)}`, '_blank');

      setTimeout(() => setSubmitStatus(''), 3000);
    } catch (error) {
      console.error('Failed to submit form:', error);
      setSubmitStatus('error');
      setTimeout(() => setSubmitStatus(''), 3000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const callSalon = () => {
    window.open(`tel:${contactInfo.phone}`, '_self');
  };

  const whatsappSalon = () => {
    const whatsappNumber = contactInfo.whatsapp.replace(/[^\d]/g, '');
    window.open(`https://wa.me/${whatsappNumber}`, '_blank');
  };

  const emailSalon = () => {
    window.open(`mailto:${contactInfo.email}`, '_self');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-400 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading contact information...</p>
        </div>
      </div>
    );
  }


  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section
        className="relative py-32 bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(rgba(0,0,0,0.6), rgba(0,0,0,0.6)), url('${resolveImageUrl(pageSettings.hero_image_url) || contactHeroImage}')`
        }}
      >
        <div className="container text-center">
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 text-shadow-luxury animate-fadeInUp">
            {pageSettings.hero_title || "Get in Touch"}
          </h1>
          <p className="text-xl md:text-2xl text-gray-300 max-w-4xl mx-auto leading-relaxed animate-fadeInUp" style={{ animationDelay: '0.2s' }}>
            {pageSettings.hero_subtitle || "Ready to experience premium grooming? Contact us today!"}
          </p>
        </div>
      </section>

      <div className="container py-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Contact Form */}
          <div className="animate-fadeInLeft">
            <div className="bg-white rounded-2xl shadow-luxury p-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center">
                <i className="ri-mail-line text-primary-400 mr-3"></i>
                Send us a Message
              </h2>

              {submitStatus === 'success' && (
                <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg mb-6">
                  <i className="ri-check-circle-line mr-2"></i>
                  Thank you! Your message has been sent successfully.
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg mb-6">
                  <i className="ri-error-warning-line mr-2"></i>
                  Sorry, there was an error sending your message. Please try again.
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-700 font-semibold mb-2">Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent text-sm"
                      placeholder="Enter your full name"
                    />
                  </div>

                  <div>
                    <label className="block text-gray-700 font-semibold mb-2">Phone *</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent text-sm"
                      placeholder="Enter your phone number"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-gray-700 font-semibold mb-2">Email</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent text-sm"
                    placeholder="Enter your email address"
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-semibold mb-2">Service Interest</label>
                  <select
                    name="service"
                    value={formData.service}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent text-sm pr-8"
                  >
                    <option value="">Select a service</option>
                    {services.map((service) => (
                      <option key={service} value={service}>{service}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-gray-700 font-semibold mb-2">Message</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    rows={5}
                    maxLength={500}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent text-sm resize-none"
                    placeholder="Tell us about your requirements or questions..."
                  ></textarea>
                  <p className="text-sm text-gray-500 mt-1">{formData.message.length}/500 characters</p>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-primary-400 hover:bg-primary-500 text-black py-4 rounded-full font-bold text-lg transition-all duration-300 btn-hover-effect whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin mr-2"></i>
                      Sending...
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line mr-2"></i>
                      Send Message
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>

          {/* Contact Info */}
          <div className="animate-fadeInRight">
            <div className="space-y-8">
              {/* Quick Contact */}
              <div className="bg-white rounded-2xl shadow-luxury p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                  <i className="ri-phone-line text-primary-400 mr-3"></i>
                  Quick Contact
                </h3>

                <div className="space-y-4">
                  <div className="flex items-center space-x-4 p-4 bg-gray-50 rounded-lg">
                    <div className="w-12 h-12 bg-primary-400 rounded-full flex items-center justify-center">
                      <i className="ri-phone-fill text-black text-lg"></i>
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-gray-900">Call Us</p>
                      <button
                        onClick={callSalon}
                        className="text-primary-400 hover:text-primary-500 transition-colors cursor-pointer"
                      >
                        {contactInfo.phone}
                      </button>
                    </div>
                  </div>

                  <div className="flex items-center space-x-4 p-4 bg-gray-50 rounded-lg">
                    <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center">
                      <i className="ri-whatsapp-fill text-white text-lg"></i>
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-gray-900">WhatsApp</p>
                      <button
                        onClick={whatsappSalon}
                        className="text-green-600 hover:text-green-700 transition-colors cursor-pointer"
                      >
                        Chat with us
                      </button>
                    </div>
                  </div>

                  <div className="flex items-center space-x-4 p-4 bg-gray-50 rounded-lg">
                    <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center">
                      <i className="ri-mail-fill text-white text-lg"></i>
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-gray-900">Email</p>
                      <button
                        onClick={emailSalon}
                        className="text-blue-600 hover:text-blue-700 transition-colors cursor-pointer"
                      >
                        {contactInfo.email}
                      </button>
                    </div>
                  </div>

                  {contactInfo.address && (
                    <div className="flex items-start space-x-4 p-4 bg-gray-50 rounded-lg">
                      <div className="w-12 h-12 bg-gray-500 rounded-full flex items-center justify-center">
                        <i className="ri-map-pin-fill text-white text-lg"></i>
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-gray-900">Address</p>
                        <p className="text-gray-600 text-sm">{contactInfo.address}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Business Hours */}
              <div className="bg-white rounded-2xl shadow-luxury p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                  <i className="ri-time-line text-primary-400 mr-3"></i>
                  Business Hours
                </h3>

                <div className="space-y-3">
                  <div className="flex justify-between items-center p-4 bg-primary-50 rounded-lg border border-primary-200">
                    <span className="text-gray-700 font-semibold">Monday - Sunday</span>
                    <span className="font-bold text-primary-400 text-lg">8:00 AM - 9:00 PM</span>
                  </div>
                  <div className="text-sm text-gray-600 text-center">
                    <i className="ri-information-line text-primary-400 mr-1"></i>
                    Open every day of the week. No appointment needed, but booking recommended for priority service.
                  </div>
                </div>
              </div>

              {/* Social Media */}
              <div className="bg-white rounded-2xl shadow-luxury p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                  <i className="ri-share-line text-primary-400 mr-3"></i>
                  Follow Us
                </h3>

                <div className="flex space-x-4">
                  {contactInfo.facebook_url && (
                    <button
                      onClick={() => window.open(contactInfo.facebook_url, '_blank')}
                      className="w-12 h-12 bg-blue-600 hover:bg-blue-700 text-white rounded-full flex items-center justify-center transition-all duration-300 cursor-pointer"
                    >
                      <i className="ri-facebook-fill"></i>
                    </button>
                  )}
                  {contactInfo.instagram_url && (
                    <button
                      onClick={() => window.open(contactInfo.instagram_url, '_blank')}
                      className="w-12 h-12 bg-pink-600 hover:bg-pink-700 text-white rounded-full flex items-center justify-center transition-all duration-300 cursor-pointer"
                    >
                      <i className="ri-instagram-fill"></i>
                    </button>
                  )}
                  {contactInfo.twitter_url && (
                    <button
                      onClick={() => window.open(contactInfo.twitter_url, '_blank')}
                      className="w-12 h-12 bg-blue-400 hover:bg-blue-500 text-white rounded-full flex items-center justify-center transition-all duration-300 cursor-pointer"
                    >
                      <i className="ri-twitter-fill"></i>
                    </button>
                  )}
                  <button
                    onClick={whatsappSalon}
                    className="w-12 h-12 bg-green-500 hover:bg-green-600 text-white rounded-full flex items-center justify-center transition-all duration-300 cursor-pointer"
                  >
                    <i className="ri-whatsapp-fill"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Locations Section */}
      <section className="py-20 bg-gray-50">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="text-primary-400 text-lg font-semibold mb-4 tracking-wide uppercase">
              Our Locations
            </h2>
            <h3 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Visit Our Branches
            </h3>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Find the nearest Fully A/C Salon location and experience premium grooming services.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {locations.length > 0 ? locations.map((location, index) => (
              <div
                key={location.id}
                className={`bg-white rounded-2xl shadow-lg overflow-hidden animate-fadeInUp`}
                style={{ animationDelay: `${index * 0.2}s` }}
              >
                <div className="h-64">
                  <iframe
                    src={location.map_url}
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title={location.name}
                  ></iframe>
                </div>

                <div className="p-6">
                  <h4 className="text-xl font-bold text-gray-900 mb-3">{location.name}</h4>

                  <div className="space-y-3">
                    <div className="flex items-start space-x-3">
                      <i className="ri-map-pin-line text-primary-400 mt-1"></i>
                      <span className="text-gray-700 text-sm">{location.address}</span>
                    </div>

                    <div className="flex items-center space-x-3">
                      <i className="ri-phone-line text-primary-400"></i>
                      <button
                        onClick={callSalon}
                        className="text-gray-700 hover:text-primary-400 transition-colors cursor-pointer"
                      >
                        {location.phone}
                      </button>
                    </div>

                    {location.email && (
                      <div className="flex items-center space-x-3">
                        <i className="ri-mail-line text-primary-400"></i>
                        <span className="text-gray-700">{location.email}</span>
                      </div>
                    )}

                    {location.hours && (
                      <div className="flex items-center space-x-3">
                        <i className="ri-time-line text-primary-400"></i>
                        <span className="text-gray-700">{location.hours}</span>
                      </div>
                    )}
                  </div>

                  <div className="mt-6 flex space-x-3">
                    <button
                      onClick={() => window.open(`https://maps.google.com/search/${encodeURIComponent(location.address)}`, '_blank')}
                      className="flex-1 bg-primary-400 hover:bg-primary-500 text-black py-2 px-4 rounded-full font-semibold transition-all duration-300 btn-hover-effect text-center whitespace-nowrap cursor-pointer"
                    >
                      Get Directions
                    </button>
                    <button
                      onClick={whatsappSalon}
                      className="bg-green-500 hover:bg-green-600 text-white p-2 rounded-full transition-all duration-300 cursor-pointer"
                    >
                      <i className="ri-whatsapp-fill text-lg"></i>
                    </button>
                  </div>
                </div>
              </div>
            )) : (
              <div className="col-span-3 text-center py-12">
                <i className="ri-map-pin-line text-6xl text-gray-300 mb-4"></i>
                <p className="text-xl text-gray-500">No locations available at the moment.</p>
                <p className="text-gray-400">Please contact us directly for location information.</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 luxury-gradient">
        <div className="container text-center">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Ready to Experience Premium Grooming?
            </h2>
            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
              Contact us today to book your appointment or visit any of our convenient locations.
              We're here to provide you with the best grooming experience.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={whatsappSalon}
                className="bg-primary-400 hover:bg-primary-500 text-black px-8 py-4 rounded-full font-bold text-lg transition-all duration-300 btn-hover-effect whitespace-nowrap cursor-pointer"
              >
                Book on WhatsApp
              </button>
              <button
                onClick={callSalon}
                className="border-2 border-primary-400 text-primary-400 hover:bg-primary-400 hover:text-black px-8 py-4 rounded-full font-bold text-lg transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                Call Now
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;